import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { CopyButton } from "components/CopyButton/CopyButton";
import { SkeletonBlock } from "components/general/Skeleton";
import { Spacer } from "components/general/Spacer/Spacer";
import { Text } from "components/general/Text";
import { DUMMY_WALLET } from "config/consts";
import { FlexBetweenContainer } from "containers/FlexBetweenContainer";
import { useFetchEnsAvatar, useFetchEnsName } from "hooks/queries/ens-queries";
import { PropsWithChildren, useMemo, useState } from "react";
import { GlobalStore } from "store/global/GlobalStore";
import { TransactionDetail } from "types/history";
import { formatAddress, formatTxHash } from "utils/walletUtils";
import { Hash } from "viem";
import { CHAINS } from "config/chains";

interface ActivityDetailDescriptionProps {
  chainId: TransactionDetail["chain_id"];
  fee: TransactionDetail["gasFeeUsd"];
  txHash: TransactionDetail["txHash"];
  sender: TransactionDetail["sender"];
  receiver: TransactionDetail["receiver"];
}

export const ActivityDetailDescription = ({
  chainId,
  fee,
  txHash,
  sender,
  receiver,
}: ActivityDetailDescriptionProps) => {
  const theme = GlobalStore.useState(s => s.theme);
  const [isTxHashCopied, setIsTxHashCopied] = useState(false);
  const networkName = CHAINS[chainId].displayName;
  const selectedNetworkConfig = CHAINS[chainId];
  const NetworkIcon = selectedNetworkConfig.icon;

  return (
    <Wrapper>
      <TradeAndSendDescription sender={sender} receiver={receiver} chainId={chainId} />
      <Row title="Network">
        <NetworkIcon width={16} height={16} />
        <Spacer width="2px" />
        <Text textStyle="Paragraph" size="Large">
          {networkName}
        </Text>
      </Row>
      <Row title={`Included ${networkName} Fee`}>
        <Text textStyle="Paragraph" size="Large">
          ${fee.toFixed(2)}
        </Text>
      </Row>
      <Row title="Transaction">
        <CopyButton
          buttonType={"link"}
          isCopied={isTxHashCopied}
          setIsCopied={setIsTxHashCopied}
          copyValue={txHash}
          iconPlacement="right"
          iconSize={16}
          iconGap={8}
          buttonCss={css`
            color: ${theme.button.primary.text.default};
            transition: color 250ms linear;

            &:hover {
              color: ${theme.button.primary.text.hover};
            }
          `}
        >
          {formatTxHash(txHash as Hash)}
        </CopyButton>
      </Row>
    </Wrapper>
  );
};

// Sub-components

const Wrapper = styled.div`
  padding: 0 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 15px;
`;

const Row = ({ title, children }: PropsWithChildren<{ title: string }>) => {
  const theme = GlobalStore.useState(s => s.theme);
  return (
    <FlexBetweenContainer>
      <Text textStyle="Paragraph" size="Large" color={theme.font.secondary.default}>
        {title}
      </Text>
      <FlexBetweenContainer>{children}</FlexBetweenContainer>
    </FlexBetweenContainer>
  );
};

const EnsAvatarContainer = styled.img`
  width: 12px;
  height: 12px;
  border-radius: 50px;
  position: relative;
  top: -1px;
`;

interface TradeAndSendDescriptionProps {
  sender: TransactionDetail["sender"];
  receiver: TransactionDetail["receiver"];
  chainId: TransactionDetail["chain_id"];
}

const TradeAndSendDescription = ({ sender, receiver, chainId }: TradeAndSendDescriptionProps) => {
  const address = GlobalStore.useState(s => s.address);
  const isSender = address === sender;
  const title = isSender ? "Sent to" : "Received from";
  const addressToDisplay = isSender ? receiver : sender;

  const { data: ensName, isLoading: ensNameIsLoading } = useFetchEnsName({
    address: addressToDisplay,
    chainId,
  });
  const { data: ensAvatarUrl, isLoading: ensAvatarIsLoading } = useFetchEnsAvatar({
    name: ensName,
    chainId,
  });

  const addressOrEnsName = useMemo(() => {
    if (ensNameIsLoading) {
      return <SkeletonBlock width={74} height={14} />;
    }
    return ensName ?? formatAddress(addressToDisplay ?? DUMMY_WALLET);
  }, [addressToDisplay, ensName, ensNameIsLoading]);

  const ensAvatar = useMemo(() => {
    if (ensAvatarIsLoading || ensAvatarUrl == null) return null;
    return (
      <>
        <EnsAvatarContainer src={ensAvatarUrl} width={12} height={12} alt="ENS Avatar" />
        <Spacer width={4} />
      </>
    );
  }, [ensAvatarIsLoading, ensAvatarUrl]);

  if (sender === receiver) return null;

  return (
    <Row title={title}>
      {ensAvatar}
      <Text textStyle="Paragraph" size="Large">
        {addressOrEnsName}
      </Text>
    </Row>
  );
};
